// client/src/dnd/emitter_tool/engine/walls.js
import * as PIXI from "pixi.js";

/* ---------- shared geometry ---------- */
function computeWallPolysFromSize(w, h) {
  const barWidth = Math.max(14, Math.round(w * 0.012));
  const gap = Math.max(80, Math.round(h * 0.12));
  const margin = Math.max(30, Math.round(h * 0.08));

  const cx = Math.round(w * 0.78);
  const topY1 = margin;
  const topY2 = Math.max(margin, Math.floor(h / 2 - gap / 2));
  const botY1 = Math.min(h - margin, Math.ceil(h / 2 + gap / 2));
  const botY2 = h - margin;

  const leftX = cx - Math.floor(barWidth / 2);
  const rightX = leftX + barWidth;

  // bottom hollow square (“room”)
  const roomCenterY = Math.round((botY1 + botY2) / 2);
  const availableH = Math.max(0, botY2 - botY1);
  const availableW = Math.max(0, Math.min(w - 2 * margin, Math.floor(w * 0.34)));
  const outerSide = Math.max(100, Math.min(availableH, availableW));
  const roomX0 = Math.round(cx - outerSide / 2);
  const roomY0 = Math.round(roomCenterY - outerSide / 2);

  const polys = [
    // Top vertical bar
    [
      { x: leftX, y: topY1 },
      { x: rightX, y: topY1 },
      { x: rightX, y: topY2 },
      { x: leftX, y: topY2 },
    ],
    // Room top
    [
      { x: roomX0, y: roomY0 },
      { x: roomX0 + outerSide, y: roomY0 },
      { x: roomX0 + outerSide, y: roomY0 + barWidth },
      { x: roomX0, y: roomY0 + barWidth },
    ],
    // Room bottom
    [
      { x: roomX0, y: roomY0 + outerSide - barWidth },
      { x: roomX0 + outerSide, y: roomY0 + outerSide - barWidth },
      { x: roomX0 + outerSide, y: roomY0 + outerSide },
      { x: roomX0, y: roomY0 + outerSide },
    ],
    // Room left
    [
      { x: roomX0, y: roomY0 + barWidth },
      { x: roomX0 + barWidth, y: roomY0 + barWidth },
      { x: roomX0 + barWidth, y: roomY0 + outerSide - barWidth },
      { x: roomX0, y: roomY0 + outerSide - barWidth },
    ],
    // Room right
    [
      { x: roomX0 + outerSide - barWidth, y: roomY0 + barWidth },
      { x: roomX0 + outerSide, y: roomY0 + barWidth },
      { x: roomX0 + outerSide, y: roomY0 + outerSide - barWidth },
      { x: roomX0 + outerSide - barWidth, y: roomY0 + outerSide - barWidth },
    ],
  ];

  return { polys };
}

function polysToSegments(polys) {
  const segs = [];
  for (const poly of polys) {
    for (let i = 0; i < poly.length; i++) {
      const a = poly[i];
      const b = poly[(i + 1) % poly.length];
      segs.push({ x1: a.x, y1: a.y, x2: b.x, y2: b.y });
    }
  }
  return segs;
}

/**
 * drawCenterGapWall(app, opts?)
 * opts:
 *   - onWalls(payload)   // called with { polygons, segments, space:'image', dpr:1 }
 *   - onCttWalls(payload) / reportWalls(payload) // aliases
 *   - alsoEmitEvent=true // keep legacy window event
 */
export function drawCenterGapWall(app, opts = {}) {
  const layer = new PIXI.Container();
  layer.name = "WallLayer";
  layer.zIndex = 900;

  const fill = 0x777777;
  const alpha = 0.9;
  const stroke = 0x555555;

  const w = app?.renderer?.width ?? app?.screen?.width ?? 0;
  const h = app?.renderer?.height ?? app?.screen?.height ?? 0;

  const { polys } = computeWallPolysFromSize(w, h);

  // draw
  const g = new PIXI.Graphics();
  g.lineStyle(1, stroke, 1);
  for (const poly of polys) {
    const x = poly[0].x;
    const y = poly[0].y;
    const rectW = poly[1].x - poly[0].x;
    const rectH = poly[2].y - poly[1].y;
    g.beginFill(fill, alpha);
    g.drawRect(x, y, rectW, rectH);
    g.endFill();
  }
  layer.addChild(g);

  // payload for collisions
  const segments = polysToSegments(polys);
  const payload = { polygons: polys, segments, space: "image", dpr: 1 };

  // attach for inspection / late consumers
  layer.__emitterWalls = payload;
  if (typeof window !== "undefined") window.__EmitterWallsLast = payload;

  const notify =
    typeof opts.onWalls === "function"
      ? opts.onWalls
      : typeof opts.onCttWalls === "function"
      ? opts.onCttWalls
      : typeof opts.reportWalls === "function"
      ? opts.reportWalls
      : null;

  if (notify) {
    try {
      notify(payload);
    } catch (e) {
      // swallow
    }
  }

  // --- cross-realm broadcasts (so OverlayManager anywhere can hear) ---
  try {
    // 1) CustomEvent (same-realm)
    window.dispatchEvent(
      new CustomEvent("EmitterTool:WallsUpdated", {
        detail: { polys, segments, space: "image", dpr: 1 },
      })
    );
  } catch {}

  try {
    // 2) BroadcastChannel (all same-origin realms)
    const bc = new BroadcastChannel("EmitterTool:Walls");
    bc.postMessage({ type: "walls", payload });
    bc.close?.();
  } catch {}

  try {
    // 3) localStorage (storage event across realms)
    localStorage.setItem(
      "__ET_WALLS_LAST",
      JSON.stringify({ t: Date.now(), payload })
    );
  } catch {}

  return { layer, polys };
}

export function getWallPolys(app) {
  const w = app?.renderer?.width ?? app?.screen?.width ?? 0;
  const h = app?.renderer?.height ?? app?.screen?.height ?? 0;
  const { polys } = computeWallPolysFromSize(w, h);
  return polys;
}

export function getWallSegments(app) {
  return polysToSegments(getWallPolys(app));
}

export function emitWallsEvent(app) {
  const polys = getWallPolys(app);
  const segments = polysToSegments(polys);
  const payload = { polygons: polys, segments, space: "image", dpr: 1 };
  try {
    window.dispatchEvent(
      new CustomEvent("EmitterTool:WallsUpdated", {
        detail: { polys, segments, space: "image", dpr: 1 },
      })
    );
  } catch {}
  try {
    const bc = new BroadcastChannel("EmitterTool:Walls");
    bc.postMessage({ type: "walls", payload });
    bc.close?.();
  } catch {}
  try {
    localStorage.setItem(
      "__ET_WALLS_LAST",
      JSON.stringify({ t: Date.now(), payload })
    );
  } catch {}
  return { polys, segments };
}
